/**
 * Conceptual Pseudo-Pit (P18a · Feb 2026)
 *
 * VIZUALIZATION-ONLY pit envelope — Not an optimised pit — Not an ore
 * reserve. Every value is geometric only; no revenue / NPV / reserve
 * / scheduling maths anywhere on the server or this component.
 */
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Cube, Play, WarningCircle, DownloadSimple, Certificate,
} from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { computePseudoPit, getPseudoPit } from "@/lib/api";
import { WizardCard, StatusRow, KV, Pill, LoadingBanner } from "@/pages/wizard/_shared";


export default function PseudoPitPanel({ projectId }) {
  const qc = useQueryClient();
  const [params, setParams] = useState({
    bench_height_m:       10,
    bench_face_angle_deg: 65,
    berm_width_m:         6,
    overall_slope_deg:    45,
    max_depth_m:          "",       // let backend derive from block model
    buffer_m:             20,
  });
  const [layers, setLayers] = useState({
    benches: true, blocks: true, boreholes: true, topography: true, outline: true,
  });
  const q = useQuery({
    queryKey: ["pseudo-pit", projectId],
    queryFn: () => getPseudoPit(projectId),
    enabled: !!projectId,
    retry: false,
    staleTime: 30_000,
  });
  const genMut = useMutation({
    mutationFn: () => {
      const body = { ...params };
      if (body.max_depth_m === "" || body.max_depth_m == null) delete body.max_depth_m;
      Object.keys(body).forEach((k) => body[k] = Number(body[k]));
      return computePseudoPit(projectId, body);
    },
    onSuccess: () => {
      toast.success("Pseudo-pit generated (visualisation only)");
      qc.invalidateQueries({ queryKey: ["pseudo-pit", projectId] });
    },
    onError: (e) => toast.error(e?.response?.data?.detail || e.message || "Pseudo-pit generation failed"),
  });

  const doc = q.data;
  const setP = (k) => (e) => setParams((p) => ({ ...p, [k]: e.target.value }));

  return (
    <WizardCard title="Conceptual Pseudo-Pit"
                 subtitle="Visualisation-only geometric envelope · Not an optimised pit · Not an ore reserve"
                 testId="w-pit-panel"
                 right={<Pill tone="amber">Conceptual</Pill>}>
      <StatusRow tone="warn">
        <b>Conceptual Resource Envelope Only</b> — Not an Optimised Economic Pit — Not an Ore Reserve.
        Geometric approximation for visualisation only; does not incorporate any economic,
        geotechnical, metallurgical, mining sequence or scheduling considerations.
      </StatusRow>

      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Field label="Bench height (m)"          value={params.bench_height_m}       onChange={setP("bench_height_m")}       step="0.5" testId="w-pit-bench-h" />
        <Field label="Bench face angle (°)"      value={params.bench_face_angle_deg} onChange={setP("bench_face_angle_deg")} step="1"   testId="w-pit-bench-face" />
        <Field label="Berm width (m)"            value={params.berm_width_m}         onChange={setP("berm_width_m")}         step="0.5" testId="w-pit-berm" />
        <Field label="Overall slope (°)"         value={params.overall_slope_deg}    onChange={setP("overall_slope_deg")}    step="1"   testId="w-pit-slope" />
        <Field label="Max depth (m) — optional"  value={params.max_depth_m}          onChange={setP("max_depth_m")}          step="1"   testId="w-pit-depth" />
        <Field label="Domain buffer (m)"         value={params.buffer_m}             onChange={setP("buffer_m")}             step="1"   testId="w-pit-buffer" />
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button size="sm" onClick={() => genMut.mutate()}
                   disabled={genMut.isPending}
                   className="bg-brand-blue text-white h-8 text-[11px]"
                   data-testid="w-pit-generate">
          <Play size={11} className="mr-1" />
          {genMut.isPending ? "Generating…" : doc ? "Regenerate" : "Generate pseudo-pit"}
        </Button>
        {doc && (
          <Button size="sm" variant="outline" className="h-8 text-[11px]"
                     onClick={() => { navigator.clipboard.writeText(JSON.stringify(doc, null, 2)); toast.success("Pit definition copied to clipboard"); }}
                     data-testid="w-pit-copy-json">
            <DownloadSimple size={11} className="mr-1" /> Export JSON
          </Button>
        )}
      </div>

      {genMut.isPending && <LoadingBanner label="Computing pit envelope…" />}
      {q.isLoading && <LoadingBanner label="Loading existing pit…" />}

      {doc && (
        <>
          {/* Layer toggles for the visualisation */}
          <div className="rounded border border-slate-200 bg-slate-50 p-2 text-[11px]" data-testid="w-pit-layers">
            <div className="text-[9px] font-mono uppercase tracking-widest text-slate-500 mb-1">Display layers</div>
            <div className="flex flex-wrap gap-3">
              {Object.entries(layers).map(([k, v]) => (
                <label key={k} className="inline-flex items-center gap-1 cursor-pointer text-slate-700">
                  <input type="checkbox" checked={v}
                          onChange={(e) => setLayers((l) => ({ ...l, [k]: e.target.checked }))}
                          data-testid={`w-pit-layer-${k}`} />
                  <span className="capitalize">{k}</span>
                </label>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <KV k="Benches"           v={doc.n_benches} />
            <KV k="Footprint area"    v={`${(doc.footprint_area_m2 / 1e6).toFixed(3)} km²`} />
            <KV k="Shell volume"      v={`${(doc.est_shell_volume_m3 / 1e6).toFixed(3)} Mm³`} />
            <KV k="Max depth"         v={`${doc.inputs.max_depth_m} m`} />
            <KV k="Overall slope"     v={`${doc.inputs.overall_slope_deg}°`} />
            <KV k="Face angle"        v={`${doc.inputs.bench_face_angle_deg}°`} />
            <KV k="Berm width"        v={`${doc.inputs.berm_width_m} m`} />
            <KV k="Domain buffer"     v={`${doc.inputs.buffer_m} m`} />
          </div>

          {/* Plan-view preview using SVG (top-down) — bench outlines from doc.benches[] */}
          <PitPlanView doc={doc} layers={layers} />

          <div className="text-[10px] text-slate-500 italic">{doc.disclaimer}</div>
        </>
      )}

      {!doc && !q.isLoading && !genMut.isPending && (
        <div className="text-[12px] text-slate-500">
          No pit computed yet. Set the geotechnical parameters above and click{" "}
          <b>Generate pseudo-pit</b>.
        </div>
      )}
    </WizardCard>
  );
}


function Field({ label, value, onChange, step, testId }) {
  return (
    <div>
      <Label className="text-[10px] font-semibold text-slate-600">{label}</Label>
      <Input type="number" step={step} value={value}
              onChange={onChange}
              className="h-8 text-[12px] font-mono"
              data-testid={testId} />
    </div>
  );
}


// ── SVG plan-view (top-down) preview ────────────────────────────────
function PitPlanView({ doc, layers }) {
  const { footprint, benches } = doc;
  if (!footprint || !benches?.length) return null;
  const w = 640, h = 320, pad = 24;
  const dx = footprint.xmax - footprint.xmin;
  const dy = footprint.ymax - footprint.ymin;
  const s = Math.min((w - pad * 2) / dx, (h - pad * 2) / dy);
  const xw = (x) => pad + (x - footprint.xmin) * s;
  const yw = (y) => h - pad - (y - footprint.ymin) * s;   // flip so N is up
  const nB = benches.length;
  return (
    <div className="rounded border border-slate-200 bg-white p-2" data-testid="w-pit-preview">
      <div className="flex items-center gap-2 mb-1">
        <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Plan view</span>
        <Pill tone="amber">Conceptual · Not to scale</Pill>
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-64">
        {/* Footprint outline */}
        {layers.outline && (
          <rect x={xw(footprint.xmin)} y={yw(footprint.ymax)}
                 width={dx * s} height={dy * s}
                 fill="rgba(148,163,184,0.05)" stroke="#334155" strokeWidth="1" />
        )}
        {/* Bench nested outlines shrinking with depth (opposite: bench i widens with depth) */}
        {layers.benches && benches.map((b, i) => {
          const fade = i / (nB - 1 || 1);
          const bdx = b.xmax - b.xmin;
          const bdy = b.ymax - b.ymin;
          const alpha = 0.05 + fade * 0.20;
          return (
            <rect key={i}
                    x={xw(b.xmin)} y={yw(b.ymax)}
                    width={bdx * s} height={bdy * s}
                    fill={`rgba(217,119,6,${alpha})`}
                    stroke={`rgba(180,83,9,${0.3 + fade * 0.4})`}
                    strokeWidth="0.75" />
          );
        })}
        {/* N compass */}
        <g transform={`translate(${w - 32},${pad + 4})`}>
          <text fontSize="10" fill="#334155" fontFamily="monospace" textAnchor="middle">N ↑</text>
        </g>
        <text x={pad} y={h - 4} fontSize="9" fill="#64748B" fontFamily="monospace">
          {benches.length} benches · footprint {(dx / 1000).toFixed(2)} × {(dy / 1000).toFixed(2)} km
        </text>
      </svg>
    </div>
  );
}
